import React, { useEffect, useContext, FunctionComponent } from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';

import { parse, stringifyNode } from '../../util/api.util';
import { isEmpty } from '../../util/object.util';
import { coreapi } from '../../api';
import { AuthStore, updateAuth } from '../../context';
import { history } from '../../history';
import { useToaster, useAsyncEffect } from '../../hooks';
import { FullPageLoader } from '../../components';

export const SsoPage: FunctionComponent = () => {
  const { state, dispatch } = useContext(AuthStore);
  const { status: authStatus, auth } = state;
  const { toaster, toast } = useToaster();
  const { search, pathname } = useLocation();
  const { params } = useRouteMatch<{ token: string }>();

  useAsyncEffect(async () => {
    let token: string;
    const query = parse(search);

    if (params && params.token) {
      // /sso/magiclink/:magicLinkToken
      token = params.token;
    } else if (query.token) {
      // /sso/magiclink?token=:magicLinkToken
      token = query.token;
    } else if (query.jwt) {
      // /sso?jwt=:jwtToken
      token = query.jwt;
    } else if (query['jwt[]']) {
      // /sso?jwt[]=:jwtToken or /sso?jwt%5B%5D=:jwtToken
      const tokens = query['jwt[]'];
      token = Array.isArray(tokens) ? tokens[0] : tokens;
    }

    const isMagicLink = pathname.includes('magiclink');

    try {
      if (isMagicLink) {
        const res = await coreapi.post(token!, `/magiclink/${token!}`);
        localStorage.setItem('selfreg.authmethod', 'magiclink');
        dispatch(updateAuth(res));
      } else {
        const res = await coreapi.get(token!, '/self/list');
        dispatch(updateAuth(res));
      }
    } catch (error) {
      if (isMagicLink) {
        const message =
          'The link you are attempting to use has expired. Please request a new link here.';

        history.push(
          `/magiclink/send/${token!}?message=${encodeURIComponent(message)}`,
        );
      } else {
        toaster(toast.error(error, 'There was a problem authenticating.'));
        history.push('/logout');
      }
    }
  }, [search, params]);

  useEffect(() => {
    // only redirect to appliaction once we have fresh (not localStorage) auth
    if (authStatus === 'authed.fresh') {
      let { redirect, loginRedirect, ...query } = parse(search);
      delete query['jwt[]'];
      delete query['token'];
      // make sure there's a leading / on the redirect
      redirect = `/${(redirect || '').replace(/^\//, '')}`;
      loginRedirect = `/${(loginRedirect || '').replace(/^\//, '')}`;
      let redirectRoute = redirect !== '/' ? redirect : loginRedirect;
      if (!isEmpty(query)) redirectRoute += `?${stringifyNode(query)}`;
      const merchantId = auth?.merchant?.id;
      if (merchantId !== undefined) {
        localStorage.setItem(`selfreg.pathname.${merchantId}`, redirectRoute);
      }
      history.replace(redirectRoute);
    }
  }, [search, authStatus, auth]);

  return <FullPageLoader id="sso-page-loader" />;
};
